import React, { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Row,
  Col,
  Typography,
  Table,
} from 'antd';
import { Link } from 'react-router-dom';
import {
  fetchDashboardData,
  filterUsersByDate,
  countUsersByStatus,
  countRolesWithUsers,
  DashboardData,
} from './dashboard.config';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserAlt, faUserAltSlash, faUserCircle, faUsers, faUserShield, faUserTimes } from '@fortawesome/free-solid-svg-icons';
import { faUser } from '@fortawesome/free-regular-svg-icons';
import { Header } from 'antd/es/layout/layout';  

const { Content } = Layout;
const { Title } = Typography;

const DashboardPage = () => {
  const [dashboardState, setDashboardState] = useState<DashboardData | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchDashboardData();
      setDashboardState(data);
    };
    fetchData();
  }, []);

  if (!dashboardState) {
    return <div>Loading...</div>;
  }

  const { users, roles } = dashboardState;

  // Calculate metrics
  const totalUsers = users.length;
  const activeUsers = countUsersByStatus(users, 'Active');
  const inactiveUsers = countUsersByStatus(users, 'Inactive');

  const totalRoles = roles.length;
  const activeRoles = roles.filter(role => role.status === 'Active').length;
  const inactiveRoles = roles.filter(role => role.status === 'Inactive').length;

  const lastWeekUsers = filterUsersByDate(users, dayjs().subtract(7, 'days'));
  const lastMonthUsers = filterUsersByDate(users, dayjs().subtract(1, 'month'));
  const lastThreeMonthsUsers = filterUsersByDate(users, dayjs().subtract(3, 'months'));

  const rolesWithUserCounts = countRolesWithUsers(users);

  // Calculate percentages for 'Active | Inactive' (rounded to whole numbers)
  const activeUserPercentage = Math.round((activeUsers / totalUsers) * 100);
  const inactiveUserPercentage = Math.round((inactiveUsers / totalUsers) * 100);

  const activeRolePercentage = Math.round((activeRoles / totalRoles) * 100);
  const inactiveRolePercentage = Math.round((inactiveRoles / totalRoles) * 100);

  // Stats and Data
  const stats = [
    { title: 'Total Users', value: totalUsers, icon: <FontAwesomeIcon icon={faUser} />, link: '/accounts/users' },
    { title: 'Active Users', value: activeUsers, icon: <FontAwesomeIcon icon={faUserAlt} />, link: '/accounts/users?status=[Active,equals]' },
    { title: 'Inactive Users', value: inactiveUsers, icon: <FontAwesomeIcon icon={faUserAltSlash} />, link: '/accounts/users?status=[Inactive,equals]' },
    {
      title: 'Active | Inactive',
      value: `${activeUserPercentage}% | ${inactiveUserPercentage}%`,
      icon: <FontAwesomeIcon icon={faUserShield} />,
      link: '/accounts/users',
    },
  ];

  const advancedStats = [
    { title: 'Total Roles', value: totalRoles, icon: <FontAwesomeIcon icon={faUsers} />, link: '/accounts/roles' },
    { title: 'Active Roles', value: activeRoles, icon: <FontAwesomeIcon icon={faUserCircle} />, link: '/accounts/roles?status=[Active,equals]' },
    { title: 'Inactive Roles', value: inactiveRoles, icon: <FontAwesomeIcon icon={faUserTimes} />, link: '/accounts/roles?status=[INActive,equals]' },
    {
      title: 'Active | Inactive',
      value: `${activeRolePercentage}% | ${inactiveRolePercentage}%`,
      icon: <FontAwesomeIcon icon={faUserShield} />,
      link: '/accounts/roles',
    },
  ];

  return (
    <Layout className="min-h-screen bg-white">
      <Header className="bg-white px-6 sticky top-0 z-50 border-b border-gray-200">
        <Title level={3} className="m-0">Accounts Dashboard</Title>
      </Header>

      <Content className="px-2"> 
        <Row gutter={[24, 24]} className="mt-6">
          {stats.map((stat, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <Link to={stat.link}>
                <Card>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm text-gray-500">{stat.title}</div>
                      <div className="text-2xl font-semibold">{stat.value}</div>
                    </div>
                    <div className="text-3xl text-blue-500">
                      {stat.icon}
                    </div>
                  </div>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>

        <Row gutter={[24, 24]} className="mt-6">
          {advancedStats.map((stat, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <Link to={stat.link}>
                <Card>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm text-gray-500">{stat.title}</div>
                      <div className="text-xl font-semibold">{stat.value}</div>
                    </div>
                    <div className="text-2xl text-orange-500">
                      {stat.icon}
                    </div>
                  </div>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>

        <Row gutter={[24, 24]} className="mt-10">
          <Col xs={24} md={12}>
            <Title level={4} className="mb-4">User Counts by Period</Title>
            <Table
              dataSource={[
                { key: '1', period: 'Last Week', count: lastWeekUsers.length, link: '/accounts/users' },
                { key: '2', period: 'Last Month', count: lastMonthUsers.length, link: '/accounts/users' },
                { key: '3', period: 'Last 3 Months', count: lastThreeMonthsUsers.length, link: '/accounts/users' },
              ]}
              columns={[
                { title: 'Period', dataIndex: 'period', key: 'period' },
                {
                  title: 'User Count',
                  dataIndex: 'count',
                  key: 'count',
                  render: (text, record) => <Link to={record.link}>{text}</Link>,
                },
              ]}
              pagination={false}
            />
          </Col>
          <Col xs={24} md={12}>
            <Title level={4} className="mb-4">Roles with User Counts</Title>
            <Table
              dataSource={rolesWithUserCounts.map((role, index) => ({
                key: index,
                role: role.role,
                count: role.count,
                link: '/accounts/users',
              }))}
              columns={[
                { title: 'Role', dataIndex: 'role', key: 'role' },
                {
                  title: 'User Count',
                  dataIndex: 'count',
                  key: 'count',
                  render: (text, record) => <Link to={`${record.link}?role=[${record.role},equals]`}>{text}</Link>,
                },
              ]}
              pagination={false}
            />
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default DashboardPage;
